import {
  AlertTriangle,
  Check,
  HelpCircle,
  Hourglass,
  Loader2,
  Pause,
  X,
} from 'lucide-react'
import React, { type ComponentProps } from 'react'
import { ThreeDots } from 'react-loading-icons'

import { Badge } from 'slab'
import {
  type BrokersTaskStatus,
  type BrokersWorkflowStatus,
} from 'utils/web_api.schemas'

import { ClickableBadgeWrapper } from './ClickableBadgeWrapper'

type IconProp = ComponentProps<typeof Badge>['leftIcon']
type BadgeType = ComponentProps<typeof Badge>['type']

// Workflow badge metadata
const getWorkflowBadgeMetadata = (
  status: BrokersWorkflowStatus
): { type: BadgeType; text: string; leftIcon: IconProp } => {
  // Create spinning loader component for in-progress status
  const SpinningLoader: IconProp = (props: any) => (
    <Loader2 className='animate-spin' {...props} />
  )

  switch (status) {
    case 'pending':
      return { type: 'slate', text: 'Pending', leftIcon: Hourglass }
    case 'in_progress':
      return { type: 'info', text: 'In Progress', leftIcon: SpinningLoader }
    case 'completed':
      return { type: 'success', text: 'Completed', leftIcon: Check }
    case 'canceled':
      return { type: 'critical', text: 'Canceled', leftIcon: X }
    case 'paused':
      return { type: 'warning', text: 'Paused', leftIcon: Pause }
    default:
      return { type: 'info', text: 'Unknown', leftIcon: HelpCircle }
  }
}

export const WorkflowStatusBadge = ({
  status,
  onClick,
}: {
  status: BrokersWorkflowStatus
  onClick?: (e: React.MouseEvent) => void
}) => {
  const { type, text, leftIcon } = getWorkflowBadgeMetadata(status)

  return (
    <ClickableBadgeWrapper onClick={onClick}>
      <Badge type={type} bordered leftIcon={leftIcon}>
        {text}
      </Badge>
    </ClickableBadgeWrapper>
  )
}

// Task badge metadata
const getTaskBadgeMetadata = (
  status: BrokersTaskStatus,
  isExecutable: boolean
): { type: BadgeType; text: string; leftIcon: IconProp } => {
  // Create spinning hourglass component for pending + executable tasks.
  const HourglassIcon = isExecutable
    ? (props: any) => <Hourglass className='animate-spin' {...props} />
    : Hourglass

  const ThreeDotsIcon = (props: any) => (
    <ThreeDots
      {...props}
      height={14}
      width={14}
      fill='#014971'
      stroke='#014971'
      className='mb-0.5'
    />
  )

  switch (status) {
    case 'pending':
      return { type: 'caution', text: 'Pending', leftIcon: HourglassIcon }
    case 'excluded':
      return { type: 'neutral', text: 'Excluded', leftIcon: X }
    case 'completed':
      return { type: 'success', text: 'Completed', leftIcon: Check }
    case 'failed':
      return { type: 'critical', text: 'Failed', leftIcon: AlertTriangle }
    case 'canceled':
      return { type: 'warning', text: 'Canceled', leftIcon: X }
    case 'processing':
      return { type: 'info', text: 'Processing', leftIcon: ThreeDotsIcon }
    default:
      return { type: 'slate', text: 'Unknown', leftIcon: HelpCircle }
  }
}

export const TaskStatusBadge = ({
  status,
  isExecutable,
  onClick,
}: {
  status: BrokersTaskStatus
  isExecutable: boolean
  onClick?: (e: React.MouseEvent) => void
}) => {
  const { type, text, leftIcon } = getTaskBadgeMetadata(status, isExecutable)

  return (
    <ClickableBadgeWrapper onClick={onClick}>
      <Badge type={type} bordered leftIcon={leftIcon}>
        {text}
      </Badge>
    </ClickableBadgeWrapper>
  )
}
